package com.arena_web.data.dto

import kotlinx.serialization.Serializable

@Serializable
data class CardTemplateDto(
    val id: String,
    val name: String,
    val imageUrl: String,
    val assetsBasePath: String? = null,
    val textColor: String? = null,
    val releaseMonth: String,
    val isActive: Boolean,
    val isDefault: Boolean,
    val price: String = "0.00",
    val createdAt: String
)

/**
 * Ownership row — represents a template the user owns. No avatar lives here;
 * gallery instances are returned as [ProfileCardDto].
 */
@Serializable
data class UserCardDto(
    val id: String = "",
    val name: String,
    val cardImageUrl: String,
    val assetsBasePath: String? = null,
    val textColor: String? = null,
    /**
     * Legacy field — kept for binary compatibility with older UI sites that
     * still read `userCard.avatarUrl`. The backend never populates it: real
     * avatars live on [ProfileCardDto] now. Always null in fresh responses.
     */
    val avatarUrl: String? = null,
    val isDefault: Boolean = false,
    val isOwned: Boolean = true,
    val price: String = "0",
    val templateId: String = "",
    /**
     * True when this NON-DEFAULT card is locked because the owner has no active
     * subscription. The card is preserved but not selectable — it thaws when the
     * subscription is renewed. Default cards are never frozen.
     */
    val frozen: Boolean = false
) {
    companion object {
        private const val CDN_BASE = "https://images.arenaproplay.com/"
    }

    fun cardAssetUrl(fileName: String): String? {
        val base = assetsBasePath ?: return null
        return "${CDN_BASE}${base}/$fileName"
    }
}

/**
 * A gallery instance — owned template (`userCardId`) plus a specific avatar.
 * The user can have many of these, including multiple per template.
 * `displayOrder = 0` is the main / active card.
 */
@Serializable
data class ProfileCardDto(
    val id: String,
    val userCardId: String,
    val templateId: String,
    val templateName: String,
    val cardImageUrl: String,
    val assetsBasePath: String? = null,
    val textColor: String? = null,
    val avatarUrl: String? = null,
    val displayOrder: Int,
    /**
     * True when this NON-DEFAULT card is locked because the owner has no active
     * subscription. Frozen gallery cards stay visible but can't be selected or
     * shown as the main card; they thaw once the subscription is renewed.
     */
    val frozen: Boolean = false,
) {
    companion object {
        private const val CDN_BASE = "https://images.arenaproplay.com/"
    }

    fun cardAssetUrl(fileName: String): String? {
        val base = assetsBasePath ?: return null
        return "${CDN_BASE}${base}/$fileName"
    }
}

@Serializable
data class PurchaseCardRequest(
    val cardTemplateId: String
)

@Serializable
data class CardAvatarAttemptDto(
    val id: String,
    val userCardId: String,
    val sessionId: String,
    val generatedAvatarUrl: String,
    val isConfirmed: Boolean,
    val createdAt: String
)

@Serializable
data class ConfirmCardAvatarRequest(
    val attemptId: String,
    val sessionId: String,
    /** Edit flow: replace this card's avatar in place instead of appending. */
    val profileCardId: String? = null,
)

@Serializable
data class ReorderProfileCardsRequest(
    val orderedIds: List<String>
)
